// Minimal pull-to-refresh hook for mobile PWA usage.
// Returns { pullPx, refreshing, indicator } where indicator is a node to render.
import { useEffect, useRef, useState } from 'react';

export function usePullToRefresh(onRefresh) {
  const startY = useRef(null);
  const [pullPx, setPullPx] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const TRIGGER = 70; // px to trigger refresh
    const MAX = 110;    // visual cap

    const isAtTop = () => {
      const el = document.scrollingElement || document.documentElement;
      return (el?.scrollTop || 0) <= 0;
    };

    const onTouchStart = (e) => {
      if (!isAtTop() || refreshing) return;
      startY.current = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      if (startY.current == null || refreshing) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && isAtTop()) {
        const capped = Math.min(dy * 0.5, MAX);
        setPullPx(capped);
      }
    };
    const onTouchEnd = async () => {
      if (refreshing) { startY.current = null; return; }
      const px = pullPx;
      startY.current = null;
      setPullPx(0);
      if (px >= TRIGGER) {
        setRefreshing(true);
        try { await onRefresh(); } finally {
          setTimeout(() => setRefreshing(false), 300);
        }
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [pullPx, refreshing, onRefresh]);

  const indicator = (pullPx > 0 || refreshing) ? (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 999,
      display: 'flex', justifyContent: 'center',
      pointerEvents: 'none',
      transform: `translateY(${refreshing ? 12 : Math.min(pullPx - 24, 56)}px)`,
      transition: refreshing ? 'transform 0.2s' : 'none',
    }}>
      <div style={{
        background: '#fff', color: 'var(--navy)',
        boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
        borderRadius: '999px', padding: '6px 14px',
        fontSize: '12px', fontWeight: 700, fontFamily: 'var(--fh)',
        display: 'flex', alignItems: 'center', gap: '6px',
      }}>
        {refreshing ? (
          <>
            <span style={{
              width: 12, height: 12, border: '2px solid var(--g3)',
              borderTopColor: 'var(--navy)', borderRadius: '50%',
              display: 'inline-block',
              animation: 'au-spin 0.8s linear infinite',
            }} />
            Refreshing…
          </>
        ) : pullPx >= 70 ? '↑ Release to refresh' : '↓ Pull to refresh'}
      </div>
      <style>{`@keyframes au-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  ) : null;

  return { pullPx, refreshing, indicator };
}
